/**
 * Módulo de Visualización de Reportes (Chart.js)
 * Agrupa logs de la BD y renderiza gráficos estadísticos con porcentajes.
 * Cumple con .idx-rules.md: funciones < 40 líneas, archivo < 300 líneas.
 */

import { getAllScans } from '../infra/db.js';
import { getConfig } from '../services/config.js';
import { getCurrentUser } from './router.js';

let ageChartInst = null;
let genderChartInst = null;
let flowChartInst = null;
let bouncerChartInst = null;

// Escucha el evento global para recargar los reportes
window.addEventListener('load_reports', async () => {
  try {
    const user = getCurrentUser();
    const companyId = user ? user.company_id : null;
    const scans = await getAllScans(companyId);
    const startVal = document.getElementById('report-start-date')?.value;
    const endVal = document.getElementById('report-end-date')?.value;
    const filtered = filterScansByLimaDate(scans, startVal, endVal);
    const allowed = filtered.filter(s => s.status === 'ALLOWED');
    
    updateAveragesAndAsistentes(allowed);
    renderAgeChart(allowed);
    renderGenderChart(allowed);
    renderFlowChart(allowed);
    renderBouncerChart(filtered);
    renderTables(filtered);
  } catch (error) {
    console.error('Error al generar reportes:', error);
  }
});

// Actualiza los promedios e indicadores numéricos
function updateAveragesAndAsistentes(allowed) {
  const avgAgeEl = document.getElementById('report-avg-age');
  const asistentesEl = document.getElementById('report-current-capacity');
  if (allowed.length === 0) {
    if (avgAgeEl) avgAgeEl.innerText = '0';
    if (asistentesEl) asistentesEl.innerText = '0 personas';
    return;
  }
  const sumAge = allowed.reduce((acc, curr) => acc + (curr.age || 0), 0);
  const avg = (sumAge / allowed.length).toFixed(1);
  if (avgAgeEl) avgAgeEl.innerText = `${avg} años`;
  if (asistentesEl) asistentesEl.innerText = `${allowed.length} personas`;
}

// Gráfico de edades (18-22, 23-29, 30-39, 40-59, 60+)
function renderAgeChart(allowed) {
  const counts = { '18-22': 0, '23-29': 0, '30-39': 0, '40-59': 0, '60+': 0 };
  allowed.forEach(s => {
    if (s.age >= 18 && s.age <= 22) counts['18-22']++;
    else if (s.age >= 23 && s.age <= 29) counts['23-29']++;
    else if (s.age >= 30 && s.age <= 39) counts['30-39']++;
    else if (s.age >= 40 && s.age <= 59) counts['40-59']++;
    else if (s.age >= 60) counts['60+']++;
  });
  const ctx = document.getElementById('chart-age').getContext('2d');
  if (ageChartInst) ageChartInst.destroy();
  ageChartInst = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: Object.keys(counts),
      datasets: [{
        label: 'Clientes por Rango de Edad',
        data: Object.values(counts),
        backgroundColor: ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      scales: { y: { beginAtZero: true } },
      plugins: {
        tooltip: {
          callbacks: {
            label: c => ` Clientes: ${c.parsed.y} (${allowed.length ? ((c.parsed.y / allowed.length) * 100).toFixed(1) : 0}%)`
          }
        }
      }
    }
  });
}

// Gráfico circular de Ratio de Género
function renderGenderChart(allowed) {
  const genders = { Masculino: 0, Femenino: 0 };
  allowed.forEach(s => {
    if (s.gender === 'M') genders.Masculino++;
    else if (s.gender === 'F') genders.Femenino++;
  });
  const ctx = document.getElementById('chart-gender').getContext('2d');
  if (genderChartInst) genderChartInst.destroy();
  genderChartInst = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: Object.keys(genders),
      datasets: [{
        data: Object.values(genders),
        backgroundColor: ['#3b82f6', '#ec4899']
      }]
    },
    options: {
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: c => ` ${c.label}: ${c.parsed} (${allowed.length ? ((c.parsed / allowed.length) * 100).toFixed(1) : 0}%)`
          }
        }
      }
    }
  });
}

// Gráfico de flujo de ingreso agrupado por horas
function renderFlowChart(allowed) {
  const hourlyCounts = Array(24).fill(0);
  allowed.forEach(s => {
    hourlyCounts[parseDate(s.timestamp).getHours()]++;
  });
  const labels = [], data = [];
  for (let i = 0; i < 24; i++) {
    if (hourlyCounts[i] > 0 || i === parseDate().getHours()) {
      labels.push(`${i}:00`);
      data.push(hourlyCounts[i]);
    }
  }
  const ctx = document.getElementById('chart-flow').getContext('2d');
  if (flowChartInst) flowChartInst.destroy();
  flowChartInst = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Ingresos por Hora',
        data,
        borderColor: '#10b981',
        tension: 0.3,
        fill: true,
        backgroundColor: 'rgba(16, 185, 129, 0.1)'
      }]
    },
    options: {
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: c => ` ${c.dataset.label}: ${c.parsed.y} (${allowed.length ? ((c.parsed.y / allowed.length) * 100).toFixed(1) : 0}%)`
          }
        }
      }
    }
  });
}

// Gráfico de rendimiento de porteros (escaneos por minuto)
function renderBouncerChart(scans) {
  const stats = {};
  scans.forEach(s => {
    if (!s.portero) return;
    if (!stats[s.portero]) stats[s.portero] = [];
    stats[s.portero].push(parseDate(s.timestamp).getTime());
  });
  const labels = [], speeds = [];
  Object.entries(stats).forEach(([portero, times]) => {
    times.sort((a, b) => a - b);
    const count = times.length;
    const minutes = count > 1 ? (times[count - 1] - times[0]) / 60000 : 1;
    const speed = (count / Math.max(1, minutes)).toFixed(2);
    labels.push(portero);
    speeds.push(parseFloat(speed));
  });
  const ctx = document.getElementById('chart-bouncer').getContext('2d');
  if (bouncerChartInst) bouncerChartInst.destroy();
  bouncerChartInst = new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Escaneos por Minuto',
        data: speeds,
        backgroundColor: '#f59e0b'
      }]
    },
    options: { responsive: true, indexAxis: 'y' }
  });
}

// Rellena tablas rápidas de Vips y rechazos
function renderTables(scans) {
  const config = getConfig();
  const vipCount = {};
  scans.forEach(s => {
    if (s.status === 'ALLOWED') vipCount[s.hashedDni] = (vipCount[s.hashedDni] || 0) + 1;
  });
  const vipTable = document.getElementById('table-vips-body');
  if (vipTable) {
    const vips = Object.entries(vipCount)
      .filter(([_, count]) => count >= config.frequentThreshold)
      .sort((a, b) => b[1] - a[1]);
    vipTable.innerHTML = vips.length ? vips.map(([hash, count]) => `
      <tr>
        <td data-label="DNI Anónimo (Hash)" class="font-mono text-xs">${hash.substring(0, 12)}...</td>
        <td data-label="Frecuencia">${count} veces</td>
        <td data-label="Estado"><span class="badge badge-gold">VIP</span></td>
      </tr>
    `).join('') : '<tr><td colspan="3" class="text-center text-muted">No hay VIPs.</td></tr>';
  }
  const rejections = scans.filter(s => s.status === 'REJECTED');
  const rejectTable = document.getElementById('table-rejections-body');
  if (rejectTable) {
    rejectTable.innerHTML = rejections.length ? rejections.slice(-10).reverse().map(r => `
      <tr>
        <td data-label="ID Anónimo" class="font-mono text-xs">${r.hashedDni.substring(0, 8)}...</td>
        <td data-label="Hora">${parseDate(r.timestamp).toLocaleTimeString()}</td>
        <td data-label="Motivo"><span class="badge badge-red">${r.rejectionReason}</span></td>
        <td data-label="Operador">${r.portero || 'Sistema'}</td>
      </tr>
    `).join('') : '<tr><td colspan="4" class="text-center text-muted">Sin rechazos.</td></tr>';
  }
}

// Helper para parsear fechas de MySQL o ISO de forma segura
function parseDate(timestampStr) {
  if (!timestampStr) return new Date();
  let isoStr = timestampStr.toString().trim();
  if (isoStr.includes(' ')) {
    isoStr = isoStr.replace(' ', 'T');
  }
  if (!isoStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(isoStr)) {
    isoStr += 'Z';
  }
  return new Date(isoStr);
}

// Filtra scans según zona horaria America/Lima
function filterScansByLimaDate(scans, startDate, endDate) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Lima',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  return scans.filter(s => {
    if (!s.timestamp) return false;
    let dateStr = '';
    try {
      dateStr = formatter.format(parseDate(s.timestamp));
    } catch (e) {
      return false;
    }
    return !(startDate && dateStr < startDate) && !(endDate && dateStr > endDate);
  });
}

/**
 * Inicializa los listeners de filtros en reportes.
 */
export function initReportsModule() {
  const startEl = document.getElementById('report-start-date');
  const endEl = document.getElementById('report-end-date');
  const clearEl = document.getElementById('btn-clear-date-filter');
  const reload = () => window.dispatchEvent(new Event('load_reports'));
  startEl?.addEventListener('change', reload);
  endEl?.addEventListener('change', reload);
  clearEl?.addEventListener('click', () => {
    if (startEl) startEl.value = '';
    if (endEl) endEl.value = '';
    reload();
  });
}
